/**
 * Transcript Draft Store
 *
 * Persists the IN-PROGRESS transcript (mostly live-mode committed text) to
 * localStorage as it accumulates, so a page refresh mid-dictation — e.g. a UI
 * hot-reload — doesn't silently drop what was already spoken. On next load the
 * mic button recovers any leftover draft into the input history so it can be
 * re-inserted. Completed transcriptions are already persisted separately in
 * inputHistoryStore; this covers the window before a recording finishes.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TranscriptDraft {
  text: string;
  projectPath: string;
  timestamp: number;
}

interface TranscriptDraftState {
  draft: TranscriptDraft | null;
  /** Save/replace the current in-flight transcript for a project. Empty clears. */
  setDraft: (text: string, projectPath: string) => void;
  clearDraft: () => void;
}

export const useTranscriptDraftStore = create<TranscriptDraftState>()(
  persist(
    (set) => ({
      draft: null,
      setDraft: (text, projectPath) =>
        set({
          draft: text.trim()
            ? { text: text.trim(), projectPath, timestamp: Date.now() }
            : null,
        }),
      clearDraft: () => set({ draft: null }),
    }),
    { name: 'ateliercode-transcript-draft' },
  ),
);
